import {
  AutoSuggestAddress,
  AutoSuggestAddressEntryMode,
  AutoSuggestAddressModel as KiamaAddressModel,
  AutoSuggestAddressProps,
  KiamaField,
} from '@onegov/react-component-library';
import { useMemo, useRef } from 'react';
import {
  FieldErrors,
  useController,
  UseControllerProps,
  useFormContext,
  get,
  FieldPath,
  FieldValues,
  ValidationRule,
} from 'react-hook-form';
import type {
  AddressModel,
  AutoAddressErrorKeys,
  AutoSuggestAddressModelUnpacked,
  FormAutoSuggestAddressErrorMessages,
  UnverifiedAddress,
} from './types';
import type { FormFieldProps } from '../types';
import { AutoSuggestAddressManualFields } from './FormAutoSuggestAddressManual';
import { useLatest } from 'react-use';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<FormFieldProps<TFieldValues, TName>, 'rules' | 'errorMode'> &
  Pick<AutoSuggestAddressProps, 'label' | 'helpMessage' | 'addressType' | 'api' | 'debounceMillis' | 'initialMode'> & {
    required?: boolean;
    errorMessages?: FormAutoSuggestAddressErrorMessages;
    hideManualAddressPropertyNameField?: boolean;
    manualStreetAddressMaxLength?: ValidationRule<number>;
    manualSuburbMaxLength?: ValidationRule<number>;
  };

export const FormAutoSuggestAddress = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    id,
    name,
    required,
    shouldUnregister,
    initialMode,
    errorMessages,
    addressType,
    hideManualAddressPropertyNameField = true,
    manualStreetAddressMaxLength,
    manualSuburbMaxLength,
    ...componentProps
  } = props;

  const entryModeRef = useRef<AutoSuggestAddressEntryMode>(initialMode ?? 'auto');
  const handleEntryModeChange = (entryMode: AutoSuggestAddressEntryMode) => (entryModeRef.current = entryMode);

  const formContext = useFormContext();

  const {
    field: { value: formValue, onChange, onBlur },
    formState: { errors },
  } = useController({
    name,
    shouldUnregister,
    rules: useValidationRules(entryModeRef.current, required, errorMessages),
  });

  // Remember the most recent fully-validated auto address (one carrying an `nswPointId`), including any address loaded
  // from saved form state. When the user switches to manual mode and back without editing, the Kiama control drops the
  // `nswPointId`; this lets us restore it so a previously selected address doesn't become invalid.
  const lastValidAutoAddressRef = useRef<AddressModel | null>(null);
  const currentAddress = formValue as AddressModel;
  if (currentAddress?.nswPointId && currentAddress?.formattedAddress) {
    lastValidAutoAddressRef.current = currentAddress;
  }

  const kiamaModel = convertToKiamaModel<TFieldValues, TName>(
    entryModeRef.current,
    formValue as AddressModel,
    errors,
    name
  );

  const restoreSelectedAddress = (formModel: AddressModel): AddressModel => {
    if (entryModeRef.current !== 'auto' || formModel.nswPointId) return formModel;

    const remembered = lastValidAutoAddressRef.current;
    if (remembered?.nswPointId && remembered.formattedAddress === formModel.formattedAddress) {
      return { ...remembered };
    }

    return formModel;
  };

  const handleChange = (model: KiamaAddressModel) => {
    const formModel = convertToFormModel(entryModeRef.current, model);
    onChange(restoreSelectedAddress(formModel));
  };

  const handleSelect = (model: KiamaAddressModel) => {
    const formModel = convertToFormModel(entryModeRef.current, model, true);
    onChange(formModel);
  };

  const handleBlur = async (model: KiamaAddressModel, fieldName: string) => {
    onBlur();

    // For manual mode, we need to trigger validation on the field that was blurred, as long as the user has
    // attempted to submit the form at least once.
    if (entryModeRef.current !== 'manual' || !(formContext?.formState.submitCount > 0)) return;

    const suffix = getValidationFieldSuffix();
    if (suffix) {
      await formContext.trigger(`${name}${suffix}`);
    }

    //////////

    function getValidationFieldSuffix() {
      switch (fieldName) {
        case 'streetAddress':
        case 'suburb':
        case 'state':
        case 'postcode':
          return fieldName[0].toUpperCase() + fieldName.substring(1);

        default:
          return null;
      }
    }
  };

  return (
    <>
      <AutoSuggestAddress
        {...componentProps}
        id={id ?? name}
        value={kiamaModel}
        addressType={addressType}
        initialMode={entryModeRef.current}
        onChange={handleChange}
        onSelect={handleSelect}
        onBlur={handleBlur}
        onEntryModeChange={handleEntryModeChange}
        manualAddressProps={{ disablePropertyNameField: hideManualAddressPropertyNameField }}
      />

      <AutoSuggestAddressManualFields<TFieldValues, TName>
        name={name}
        required={required}
        addressType={addressType}
        entryMode={entryModeRef.current}
        errorMessages={errorMessages}
        manualStreetAddressMaxLength={manualStreetAddressMaxLength}
        manualSuburbMaxLength={manualSuburbMaxLength}
      />
    </>
  );
};

function useValidationRules(
  entryMode: AutoSuggestAddressEntryMode = 'auto',
  required: boolean = false,
  errorMessages: FormAutoSuggestAddressErrorMessages = {}
): UseControllerProps['rules'] {
  // The rules object only gets updated when the field is registered; as such, it won't have access to the latest
  // version of 'required', 'entryMode', or 'errorMessages'. We're using a ref so that when the validation runs,
  // it can access the latest version of each of these fields.
  const entryModeRef = useLatest(entryMode);
  const requiredRef = useLatest(required);
  const errorMessagesRef = useLatest(errorMessages);

  return useMemo((): UseControllerProps['rules'] => {
    const getErrorMessage = (errorKey: AutoAddressErrorKeys, defaultMessage: string): string =>
      errorMessagesRef.current[errorKey] ?? defaultMessage;

    // The validation rules are only required if the address is required, AND the field is currently in 'auto'
    // mode. In manual mode, a different set of validation rules run (via `FormAutoSuggestAddressManual`).
    const isAutoAddressRequired = () => requiredRef.current && entryModeRef.current === 'auto';

    return {
      validate: {
        required: (address: AddressModel) => {
          return isAutoAddressRequired() && !address?.formattedAddress
            ? getErrorMessage('required', 'Please enter a valid address.')
            : undefined;
        },

        valid: (address: AddressModel) => {
          return isAutoAddressRequired() && !address.nswPointId
            ? getErrorMessage('valid', 'Please select an address from the list, or enter an address manually.')
            : undefined;
        },
      },
    };

    // We don't need any deps for this hook since we're using refs to access the latest values of the dependencies.
  }, []);
}

function convertToKiamaModel<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(entryMode: AutoSuggestAddressEntryMode, model: AddressModel, errors: FieldErrors, name: TName): KiamaAddressModel {
  const { formattedAddress, manualAddress } = (model || {}) as AddressModel;

  return entryMode === 'auto' ? convertAutoModel() : convertManualModel();

  //////////

  function convertAutoModel() {
    return {
      formattedAddress: {
        value: formattedAddress,
        errorMessage: errors ? (get(errors, name)?.message as string) : undefined,
      },
    };
  }

  function convertManualModel() {
    const { streetAddress, propertyName, suburb, state, postcode, country } = (manualAddress ||
      {}) as UnverifiedAddress;

    const buildModelValue = (value?: string, fieldErrorKeySuffix?: string): KiamaField => {
      // Check the "errors" collection from the form to see if we can find a value for this field
      const errorMessage =
        fieldErrorKeySuffix != null ? (get(errors, `${name}${fieldErrorKeySuffix}`)?.message as string) : undefined;

      return { value, errorMessage };
    };

    return {
      streetAddress: buildModelValue(streetAddress, 'StreetAddress'),
      propertyName: buildModelValue(propertyName),
      suburb: buildModelValue(suburb, 'Suburb'),
      state: buildModelValue(state, 'State'),
      postcode: buildModelValue(postcode, 'Postcode'),
      country: buildModelValue(country),
    };
  }
}

function convertToFormModel(
  entryMode: AutoSuggestAddressEntryMode,
  model: KiamaAddressModel,
  includeNswPointId = false
): AddressModel {
  const rawAddress = unpackKiamaModel(model);

  if (entryMode === 'auto') {
    // When the text changes we need to ignore everything else in the payload object, since the Kiama control
    // sends through "stale" information (e.g. if the user selects address 'A', and then starts typing over it
    // with address 'B', the payload contains everything from address 'A', but the formatted text of address 'B').
    return {
      nswPointId: includeNswPointId ? rawAddress.nswPointId : undefined,
      formattedAddress: rawAddress.formattedAddress ?? '',
      rawAddress,
    };
  }

  const { streetAddress, propertyName, suburb, state, postcode } = rawAddress;

  return {
    rawAddress,
    formattedAddress: [streetAddress, propertyName, suburb, state, postcode]
      .map((s) => s?.trim())
      .filter((s) => !!s)
      .join(' '),
    manualAddress: {
      streetAddress,
      propertyName,
      suburb,
      state,
      postcode,
      country: 'Australia',
    },
  };
}

function unpackKiamaModel(model: KiamaAddressModel): AutoSuggestAddressModelUnpacked {
  const result: Partial<AutoSuggestAddressModelUnpacked> = {};

  Object.keys(model).forEach((key) => {
    const modelValue = (model[key as keyof KiamaAddressModel] as KiamaField<unknown>).value;
    result[key as keyof AutoSuggestAddressModelUnpacked] = modelValue as never;
  });

  return result as AutoSuggestAddressModelUnpacked;
}
